import { logger } from "../default-logger";
import { fetchAPIData, replaceParams } from "./DataFetchers";
import { spans, spanDisplayNames } from "../dataTypes/span";
import { toCamelCase } from "./StringHelpers";
import { options } from "@fullcalendar/core/preact.js";

async function fetchViewParamsOld(viewParams, projectName, selectedViewParams) {
    let count = 0;
    logger.debug('fetch params', viewParams, projectName, selectedViewParams);
    if (projectName === null || projectName === undefined) return null;
    if (viewParams === null || viewParams === undefined) return null;
    let params = {};

    // logger.debug('cur view params', viewParams);

    for (const viewParam of viewParams) {
        // let viewParam = viewParams[key];
        logger.debug('cur param', viewParam);
        let paramObject = await extractParamData(viewParam, projectName, selectedViewParams);
        if (paramObject?.type === "object") continue;
        
        if (viewParam.childParams) {
            let childParamData = [];
            for (const childParam of viewParam.childParams) {
                if (childParam?.type === "object") continue;
                // let childParam = viewParam.childParams[childKey];
                childParam['parentName'] = viewParam.name;
                
                let childParamObject = await extractParamData(childParam, projectName, selectedViewParams);
                childParamObject['parentName'] = viewParam.name;
                logger.debug('child param', childParamObject, viewParam);
                childParamData.push(childParamObject);
                count++;
            }
            paramObject['childParams'] = childParamData;
        }
        logger.debug('param object fetchViewParams', paramObject);
        params[paramObject.name] = paramObject;
        count++;
    }

    logger.debug('finish params', params, params.length);

    return {
        params: params,
        count: count
    };
};

/**
 * 
 * @param {Object} viewData 
 * @param {String} projectName 
 * @param {Object} selectedViewParams 
 * @returns {Object} {
 * params: the view param definitions with their options filled in
 * prelimParamCount:  the #of params that need to be selected before rendering the page contents. this can differ from the total number of params (e.g. a param of type marker isn't included in the count, because it's rendered as a map on the page. its selection is made after the map is rendered) )
 * }
 * 
 */
async function fetchViewParams(viewData, projectName, selectedViewParams) {
    let count = 0; //
    logger.debug('fetch params', viewData, projectName, selectedViewParams);
    if (projectName === null || projectName === undefined) return null;
    if (viewData === null || viewData === undefined) return null;
    const viewParams = viewData.parameters ?? [];
    const viewGroups = viewData.groups ?? [];
    let params = {};

    // logger.debug('cur view params', viewParams);

    for (const viewParam of viewParams) {
        // let viewParam = viewParams[key];
        logger.debug('cur param', viewParam);
        let paramObject = await extractParamData(viewParam, projectName, selectedViewParams, viewGroups);
        
        if (viewParam.childParams) {
            let childParamData = [];
            for (const childParam of viewParam.childParams) {
                // let childParam = viewParam.childParams[childKey];
                childParam['parentName'] = viewParam.name;
                
                let childParamObject = await extractParamData(childParam, projectName, selectedViewParams, viewGroups);
                childParamObject['parentName'] = viewParam.name;
                logger.debug('child param', childParamObject, viewParam);
                childParamData.push(childParamObject);
                count = childParam.type === 'object' ? count : count + 1;
            }
            paramObject['childParams'] = childParamData;
        }
        logger.debug('param object fetchViewParams', paramObject);
        params[paramObject.name] = paramObject;
        count = viewParam.type === 'object' ? count : count + 1;
    }

    logger.debug('finish params', params, count);

    return {
        params: params,
        prelimParamCount: count
    };
};

async function extractParamData(viewParam, projectName, viewParamObject, viewGroups) {
    let paramObject = {...viewParam};
    let paramOptions;

    logger.debug('extract param object', viewParam, viewParamObject);
    
    if (viewParam.type === "dateSpan") {
        paramOptions = spans;
    } else if (viewParam.type === "object") { 
        let dataSource = extractDatasource(viewParam, viewGroups);
        logger.debug('extract params datasrc', dataSource)
        if (dataSource === null || !dataSource.hasOwnProperty('expr')) return;
        logger.debug('replace params: fetchViewParams->extractParamData type object', viewParam)
        let formattedExpr = replaceParams(dataSource.expr, viewParamObject);

        logger.debug('extract params replace', formattedExpr, viewParamObject)
        paramOptions = await fetchParamOptions(formattedExpr, projectName, paramObject);
    } else if (viewParam.type === "apiExpr" || viewParam.type === "navTreeApiExpr") { 
        logger.debug('replace params: fetchViewParams->extractParamData type navTreeApiExpr', viewParam)
        let formattedExpr = replaceParams(viewParam.expr, viewParamObject);
        logger.debug('extract params replace', formattedExpr, viewParamObject)
        paramOptions = await fetchParamOptions(formattedExpr, projectName, paramObject);
    } else if (viewParam.enum) {
        let options = viewParam.enum.split(',');
        paramOptions = options.map((option) => { return {
            value: toCamelCase(option),
            // value: option,
            dis: option
        }});
    }
    if (paramOptions) paramObject[`options`] = paramOptions;
    logger.debug('cur param object', paramObject)
    return paramObject;
}

async function fetchParamOptions(expr, projectName, paramObject) {
    if (projectName === null || projectName === undefined) return [];
    const parsedProject = projectName.replace('p:', '');
    const url = `https://data.clistek.io/api/${parsedProject}/eval/?expr=`;

    let data = await fetchAPIData(url, expr, true);
    logger.debug('param api options', expr, url, data, paramObject);

    if (data?.rows) {
        let finalRows = [];
        for (const row of data.rows) {
            let option = {};
            if (paramObject.valCol) {
                if (paramObject.valCol === 'id'){
                    option['value'] = row.id?.val;
                } else {
                    console.error(`Param ${paramObject.name} has no valCol`)
                    option['value'] = row[paramObject.valCol];
                } 
            } else option['value'] = null;
            option['parentName'] = paramObject?.parentName || null;
            option['name'] = paramObject?.name || paramObject?.paramName || null;

            if (paramObject.labelCol && row[paramObject.labelCol]) {
                option['dis'] = row[paramObject.labelCol];
            } else if (row.id) {
                option['dis'] = row.id?.dis;
            } else if (row.dis) {
                option['dis'] = row.dis;
            }

            // logger.debug('test option', option);
            
            finalRows.push(option);
        }

        // logger.debug('final rows', paramObject, finalRows);
        return finalRows;
    } else return [];

}

/**
 * Given a parameter and groups, searches groups to find an object with a matching name as the parameter and returns the dataSource definition
 * @param {*} viewParam 
 * @param {*} viewGroups 
 */
const extractDatasource = (viewParam, viewGroups) => {
    logger.debug('extract datasrc start', viewParam, viewGroups);
   
    let dataSource = null;
    for (const group of viewGroups) {
        // logger.debug('cur group', group)
        let matchedObj = group.objects.find((object) => (object.paramName && object.paramName === viewParam.name));
        if (matchedObj && matchedObj.dataSource) dataSource = matchedObj.dataSource;
        // logger.debug('cur group, src', group, dataSource)
    }

    logger.debug('extract datasrc final', dataSource, viewParam);
    return dataSource;
}

/**
 * Given a viewObject and view params, finds the parameter with the matching name
 * @param {*} viewObject 
 * @param {*} viewParams 
 */
const extractParam = (viewObject, viewParams) => {
    logger.debug('extract param start', viewObject, viewParams);
    if (!viewObject.hasOwnProperty('paramName')) return null;

    let match = viewParams.find((object) => object.name === viewObject.paramName );

    if (!match) {
        for (const object of viewParams) {
            logger.debug('extract param child', object)
            if (object.childParams) {
                let childMatch = object.childParams.find((child) => child.name === viewObject.paramName );
                if (childMatch) {
                   return childMatch;
                }
            }
        }
    }

    return match;
}

/**
 * Get and fill in default values for any missing params
 * @param {*} paramData 
 * @param {*} validParams 
 * @returns 
 */
const extractDefaultParams = async (paramData, validParams) => {
    if (paramData === null || paramData === undefined) return null;

    let newParams = {...validParams};
    for (const key in paramData) {
        let param = paramData[key]; 
        logger.debug('cur default', param, key, newParams[key], paramData, validParams);
        if (newParams[key]) continue; // if the param already has a value, skip
        let defaultOption = extractDefaultParam(param);
        if (defaultOption) newParams[param.name] = defaultOption;
        
        if (param.childParams) {
            for (const childParam of param.childParams) {
                if (newParams[childParam.name]) continue;
                if (childParam.default) {
                    // newParams[childParam.name] = childParam.default;
                    let defaultOption = extractDefaultParam(childParam);
                    if (defaultOption) newParams[childParam.name] = defaultOption;
                }
            }
        }
    }
    

    // logger.debug('no params', newParams);

    return newParams;
}

const extractDefaultParam = (param) => {
    // logger.debug('extract default: start', param);
    let defaultOption = null;
    if (param.default) {
        if (param.type === 'dateSpan') {
        let defaultDis = spanDisplayNames[param.default];
        
        if (defaultDis) {
            defaultOption = {
                value: param.default,
                dis: defaultDis,
                default: param.default,
                isValidated: true,
                kind: param.kind ?? null,
                type: param.type ?? null
            };     
        }
        } else if (param.type === "enum") {
            defaultOption = {
                value: param.default,
                dis: param.default,
                default: param.default,
                isValidated: true,
                kind: param.kind ?? null,
                type: param.type ?? null
            };   

        } else if (param.type === "list" || param.type === "apiExpr") {
            let defaultObject = param.options?.find((option) => option.id.val === param.default);

            if (defaultObject) {
                defaultOption = {
                    value: param.default,
                    dis: defaultObject.dis,
                    default: param.default,
                    isValidated: true,
                    kind: param.kind ?? null,
                    type: param.type ?? null
                };     
            }
        }
    }

    // logger.debug('extract default: end', defaultOption);

    return defaultOption; 
}



export { fetchViewParams, extractParamData, extractDefaultParams, extractParam };